import {usePaldockTranslation} from "hooks/usePaldockTranslation.tsx";
import { Component, ReactNode } from "react";

import { reportErrorToFlare, toError } from "./utils";

interface ErrorUIProps {
    error: Error;
    onReset?: () => void;
}

// Shared error UI component with better design
export function ErrorUI({ error, onReset }: ErrorUIProps) {
    const { t } = usePaldockTranslation('ui.error');

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                zIndex: 9999,
            }}
        >
            <div
                style={{
                    maxWidth: '500px',
                    width: '100%',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    padding: '40px',
                    textAlign: 'center',
                }}
            >
                {/* Icon */}
                <div
                    style={{
                        width: '64px',
                        height: '64px',
                        margin: '0 auto 24px',
                        borderRadius: '50%',
                        backgroundColor: '#fee2e2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#dc2626"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                </div>

                {/* Title */}
                <h1
                    style={{
                        margin: '0 0 12px',
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#111827',
                    }}
                >
                    {t('title')}
                </h1>

                {/* Description */}
                <p
                    style={{
                        margin: '0 0 24px',
                        fontSize: '14px',
                        color: '#6b7280',
                        lineHeight: '1.6',
                    }}
                >
                    {t('description')}
                </p>

                {/* Error details in dev mode */}
                {import.meta.env.MODE !== 'production' && (
                    <div
                        style={{
                            marginBottom: '24px',
                            padding: '12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                        }}
                    >
                        <p
                            style={{
                                margin: '0',
                                fontSize: '12px',
                                color: '#dc2626',
                                fontFamily: 'monospace',
                                wordBreak: 'break-word',
                            }}
                        >
                            {error.message}
                        </p>
                    </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    {onReset && (
                        <button
                            onClick={onReset}
                            style={{
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#dc2626',
                                backgroundColor: 'white',
                                border: '1px solid #dc2626',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#fef2f2';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                            }}
                        >
                            {t('tryAgain')}
                        </button>
                    )}
                    <button
                        onClick={() => window.location.href = '/'}
                        style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'white',
                            backgroundColor: '#008080',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#006666';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#008080';
                        }}
                    >
                        {t('goToHomepage')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Router error component - handles errors within route components
export function RouterErrorComponent({ error }: { error: unknown }) {
    const err = toError(error);
    reportErrorToFlare(err, 'RouterErrorComponent');
    return <ErrorUI error={err} />;
}

// Error boundary for non-router errors (errors outside route components)
interface AppErrorBoundaryProps {
    children: ReactNode;
}

interface AppErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
    constructor(props: AppErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error) {
        reportErrorToFlare(error, 'AppErrorBoundary');
    }

    reset = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError && this.state.error) {
            return <ErrorUI error={this.state.error} onReset={this.reset} />;
        }
        return this.props.children;
    }
}
